export const RESIZE_GRID = 16;
export const RESIZE_MAX_EDGE = 3840;
export const RESIZE_MAX_ASPECT_RATIO = 3;
export const RESIZE_MIN_PIXELS = 655_360;
export const RESIZE_MAX_PIXELS = 8_294_400;

const parsePositiveNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }

  return num;
};

export const getResizeDimensionSuggestions = (value) => {
  const num = parsePositiveNumber(value);
  if (num === null) {
    return [];
  }

  const lower = Math.max(
    RESIZE_GRID,
    Math.floor(num / RESIZE_GRID) * RESIZE_GRID
  );
  const upper = Math.ceil(num / RESIZE_GRID) * RESIZE_GRID;
  const suggestions = new Set();

  if (lower <= RESIZE_MAX_EDGE && lower !== num) {
    suggestions.add(lower);
  }

  if (upper <= RESIZE_MAX_EDGE && upper !== num) {
    suggestions.add(upper);
  }

  return [...suggestions].sort((a, b) => a - b);
};

const snapUpToGrid = (value) => {
  const snapped = Math.ceil(value / RESIZE_GRID) * RESIZE_GRID;
  return Math.min(RESIZE_MAX_EDGE, Math.max(RESIZE_GRID, snapped));
};

const snapDownToGrid = (value) => {
  const snapped = Math.floor(value / RESIZE_GRID) * RESIZE_GRID;
  return Math.max(RESIZE_GRID, Math.min(RESIZE_MAX_EDGE, snapped));
};

const isAspectRatioValid = (width, height) => {
  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  return longEdge / shortEdge <= RESIZE_MAX_ASPECT_RATIO;
};

const areResizeDimensionsFullyValid = (width, height) => {
  if (
    width > RESIZE_MAX_EDGE ||
    height > RESIZE_MAX_EDGE ||
    width % RESIZE_GRID !== 0 ||
    height % RESIZE_GRID !== 0
  ) {
    return false;
  }

  return isAspectRatioValid(width, height);
};

const getAspectRatioSuggestionForPinnedField = (width, height, pinnedField) => {
  const fixedValue = pinnedField === "width" ? width : height;
  const otherField = pinnedField === "width" ? "height" : "width";
  const otherValue = pinnedField === "width" ? height : width;
  const isFixedLong = fixedValue >= otherValue;

  const suggestedValue = isFixedLong
    ? snapUpToGrid(fixedValue / RESIZE_MAX_ASPECT_RATIO)
    : snapDownToGrid(fixedValue * RESIZE_MAX_ASPECT_RATIO);

  if (suggestedValue === otherValue) {
    return null;
  }

  const nextWidth = otherField === "width" ? suggestedValue : width;
  const nextHeight = otherField === "height" ? suggestedValue : height;

  if (!isAspectRatioValid(nextWidth, nextHeight)) {
    return null;
  }

  if (!areResizeDimensionsFullyValid(nextWidth, nextHeight)) {
    return null;
  }

  return { field: otherField, value: suggestedValue, keepField: pinnedField };
};

/**
 * @param {number} width
 * @param {number} height
 * @returns {Array<{ field: "width" | "height", value: number, keepField: "width" | "height" }>}
 */
export const getAspectRatioSuggestions = (width, height) => {
  const suggestions = [];

  const adjustWidthSuggestion = getAspectRatioSuggestionForPinnedField(
    width,
    height,
    "height"
  );
  const adjustHeightSuggestion = getAspectRatioSuggestionForPinnedField(
    width,
    height,
    "width"
  );

  if (adjustWidthSuggestion) {
    suggestions.push(adjustWidthSuggestion);
  }

  if (adjustHeightSuggestion) {
    suggestions.push(adjustHeightSuggestion);
  }

  return suggestions;
};

/**
 * @param {string | number} value
 * @returns {{ valid: true, value: number } | { valid: false, reason: "invalid" | "maxEdge" | "notMultipleOf16", suggestions?: number[] }}
 */
export const validateResizeDimension = (value) => {
  const num = parsePositiveNumber(value);

  if (num === null) {
    if (value === "" || value === null || value === undefined) {
      return { valid: false, reason: "empty" };
    }

    return { valid: false, reason: "invalid" };
  }

  if (num > RESIZE_MAX_EDGE) {
    return {
      valid: false,
      reason: "maxEdge",
      suggestions: [RESIZE_MAX_EDGE],
    };
  }

  if (num % RESIZE_GRID !== 0) {
    return {
      valid: false,
      reason: "notMultipleOf16",
      suggestions: getResizeDimensionSuggestions(value),
    };
  }

  return { valid: true, value: num };
};

/**
 * @param {string | number} width
 * @param {string | number} height
 * @returns {{ valid: true, width: number, height: number, totalPixels: number } | { valid: false, reason: "aspectRatioExceeded", suggestions?: Array<{ field: "width" | "height", value: number, keepField: "width" | "height" }> } | null}
 */
export const validateResizeDimensionsGlobal = (width, height) => {
  const widthResult = validateResizeDimension(width);
  const heightResult = validateResizeDimension(height);

  if (
    !widthResult.valid ||
    !heightResult.valid ||
    widthResult.reason === "empty" ||
    heightResult.reason === "empty"
  ) {
    return null;
  }

  const normalizedWidth = widthResult.value;
  const normalizedHeight = heightResult.value;

  if (!isAspectRatioValid(normalizedWidth, normalizedHeight)) {
    return {
      valid: false,
      reason: "aspectRatioExceeded",
      suggestions: getAspectRatioSuggestions(normalizedWidth, normalizedHeight),
    };
  }

  return {
    valid: true,
    width: normalizedWidth,
    height: normalizedHeight,
    totalPixels: normalizedWidth * normalizedHeight,
  };
};

export const areResizeDimensionsReady = (width, height) =>
  validateResizeDimensionsGlobal(width, height)?.valid === true;
